export const SM_UIContent = {
  SM_Analysis: {
    title: "Analysis",
    annotationExplorer: "Highlights",
    reports: "Reports",
  },
  SM_UserManagement: {
    title: "User Management",
  },
  SM_PlatformContext: {
    title: "Platform Context",
  },
  SM_Onboarding: {
    title: "Onboarding",
    nextBtnText: "Next",
    finishBtnText: "Finish",
    skipBtnText: "Skip",
    badgeText: "Context for AI",
    screens: {
      industry: {
        title: "What industry are you in?",
        subtitle: "This helps us tailor your experience and suggest relevant metrics.",
      },
      metrics: {
        title: "What metrics matter most?",
        subtitle: "Select the signals you track, or add your own.",
      },
      journey: {
        title: "Map your product journey",
        subtitle: "Define the key steps in your user's journey. Drag to reorder, remove with X, or add your own steps.",
        placeholder: "signup → onboard → activate → retain",
      },
      personas: {
        title: "Define your user personas",
        subtitle: "Who are you building for? Add the personas you want to track insights for.",
      },
      insightPriorities: {
        title: "This helps Dots prioritize the right signals and structure insights accordingly.",
        helpText: "Pick what matters most right now. You can explore others later.",
      },
      tagHierarchy: {
        title: "Your suggested tag structure",
        subtitle: "We've generated a codebook based on your inputs. Edit themes and tags as needed before confirming.",
        regenerateBtn: "Regenerate",
      },
      dataImport: {
        title: "Import your data",
        subtitle: "Upload an Excel or CSV file to get started, or skip this step for now.",
      },
    },
  },
  SM_TCI: {
    display: {
      datasets: "Datasets",
      templateBuilder: "Template Builder",
    },
  },
  SM_DataImport: {
    display: {
      datasets: "Datasets",
      import: "Import",
    },
  },
  SM_Datasets: {
    display: {
      editTemplate: "Edit Template",
      viewTemplate: "View Template",
      deleteDataset: "Delete Dataset",
    },
    title: {
      createNewTemplate: "Create New Template",
      importNewDataset: "Import New Dataset",
      datasets: "Datasets",
    },
    tableHeaders: {
      title: "Title",
      dateCreated: "Date Created",
      status: "Status",
      documentCount: "Documents",
      annotated: "Annotated",
    },
    statusDisplayLookup: {
      unpublished: "Not Published",
      published: "Active",
      editPublished: "Active (Pending Changes)",
      importingData: "Importing Data",
      autoAnnotating: "Annotating",
    },
    confirmDeleteDataset: {
      title: "Delete this dataset?",
      onConfirmText: "Delete dataset",
      warning:
        "This will permanently delete the dataset and ALL associated data: every document, annotation, AI categorization, topic, summary, chunk, embedding, search index, role permission and the template configuration itself. This action cannot be undone.",
      typeToConfirmPrompt: "Type the dataset id to confirm:",
      importingNotice:
        "Cannot delete while data is being imported. Wait for the import to finish.",
      loaderText: "Deleting dataset...",
    },
    selfManaged_addOnModules: {
      contentPublishing: {
        addDataViaTemplate: "Add Data Via Template",
      }
    }
  },
  SM_DirectDataImport: {
    display: {
      title: "Document Title",
      previewMessage: "Only displaying first 20 rows for preview purposes",
      nameOfDataset: "Name of Dataset",
      contextForAI: "Context for AI",
      natureOFData: "Describe the nature of this data",
      importData: "Importing Data...",
      processExcel: "Processing Excel Sheet...",
      datasets: "Datasets",
      importNewDataset: "Import New Dataset",
    },
    bannerAlert: {
      warning1: "Can't have 2 title fields in the data",
      warning2: "At least 1 title field is required to import data",
      noDateField:
        "No column is set to a Date or Date & Time field. Trends, period comparisons and other time-based analyses need a date on each document, so we recommend marking one before you continue.",
      error1: "Upload failed",
    },
    tableHeaders: {
      columnHeader: "Column Header",
      fieldType: "Field Type",
    },
    folderName: "directDataImport",
    tagsSplitCharacter: "^",
    tagsSplitCharacterComma: ",",
    dropdowns: {
      dontImportShort: "Don't Import",
      dontImport: "Don't import this column",
      separationFormat: "Separation format",
      separationFormatPlaceholder: "No separation",
      tags: "Connect With Existing Tag Category",
      includeInAiAnalysis: "Include in AI Analysis",
      includeInAiAnalysisTooltip: "AI reads this field to create highlights, categories and insights. Turn off to exclude this field from AI analysis — it will still appear in search and summaries.",
      applyChanges: "Apply Changes",
    },
  },
  DirectDataImport_splitCharacter_colTooltipText:
    "For multi-select fields, a 'split character' can be set, which would be used as a marker to identify separate one option from the other. e.g 'India, China' with a split character of ',' would store 'India' and 'China' separately in the database.",
  DirectDataImport_AICat_colTooltipText:
    "When turned on, AI will analyze and categorize your data in this field, into percentage-based groups. When turned off, you’ll only get text summaries for this field. These categories & summaries can then be used in dashboards, reports, and analysis for quick insights. (This feature is only applicable on Title, Text & Rich Text Fields)",
  DirectDataImport_AICat_ComsBox: {
    title: "AI Categorization",
    span1: "When",
    span2:
      "is turned on, AI will analyze and categorize your data in this field, into percentage-based groups. When turned off, you’ll only get text summaries for this field. These categories & summaries can then be used in dashboards, reports, and analysis for quick insights.",
    span3: "This feature is only applicable on Title, Text & Rich Text Fields.",
  },
  SM_Dashboard: {
    // The Insights tab before there is anything to read, and when it fails.
    insightsEmpty: "No insights yet. They’ll appear once the first observations have been processed.",
    insightsError: "Couldn’t load insights.",
    // The Overview's Fires stat card: its reading under the count.
    firesStat: {
      none: "No urgent topics right now",
      some: "Severe and rising",
    },
    // The Overview when only one period has data: what fills in the widgets
    // that cannot say anything time-wise yet.
    singlePeriod: {
      signalTrend: "Add data from more dates to see signal trends.",
      zones: "Add data from more dates to spot what's heating up and cooling down.",
      fires: "Add data from more dates to spot what's heating up",
      emerging: "Add data from more dates to spot what's small but growing.",
    },
    // Why the "vs" period can't be picked on Priorities once there is enough
    // history: each topic is then measured against its own usual level.
    compareLocked: (count, periodsNoun) =>
      `There are observations from the last ${count} ${periodsNoun}, so priorities are worked out from that whole stretch for a more reliable picture. Comparing with one earlier period doesn't apply here.`,
  },
  SM_AIEngineManager: {
    title: "AI Engine Manager",
  },
  SM_Topics: {
    // The Topics page before the first topics exist.
    empty: "No topics yet. They’ll appear once the first observations have been processed.",
  },
  SM_ThemesManager: {
    title: "Themes Manager",
    showTagDescription: "Show Tag Descriptions",
    hideTagDescription: "Hide Tag Descriptions",
    showUnusedTags: "Show Unused Tags",
    showUsedCount: "Show Used Count",
    deleteText: {
      tags: "Delete tag?",
      subThemes: "Delete sub-theme?",
      themes: "Delete theme?",
    },
    addInputHelpText: {
      tags: {
        helpText1: "Enter to Add",
        helpText2:
          "Or type/paste tag names separated by commas or lines to add multiple tags at once",
      },
      subThemes: {
        helpText1: "Enter to Add",
        helpText2:
          "Or type/paste sub-theme names separated by commas or lines to add multiple sub-themes at once",
      },
      themes: {
        helpText1: "Enter to Add",
        helpText2:
          "Or type/paste theme names separated by commas or lines to add multiple themes at once",
      },
    },
    descInput: {
      keyCombination: "shift + enter",
      additionalText: "for new line",
    },
  },
};
